<template>
  <div class="fault-container">
    <CardBox title="故障统计" subtitle="Alarm and fault">
      <!-- 三个统计卡片 -->
      <div class="fault-cards">
        <div class="fault-card" :style="{ backgroundImage: `url(${faultItemBg})` }">
          <div class="fault-card__label">人工上报</div>
          <div class="fault-card__value">{{ errorAlarmCountStatisticsData['alarmCount'] }}</div>
        </div>
        <div class="fault-card" :style="{ backgroundImage: `url(${faultItemBg})` }">
          <div class="fault-card__label">自动上报</div>
          <div class="fault-card__value">{{ errorAlarmCountStatisticsData['errorCount'] }}</div>
        </div>
        <div class="fault-card" :style="{ backgroundImage: `url(${faultItemBg})` }">
          <div class="fault-card__label">已处理故障</div>
          <div class="fault-card__value">{{ errorAlarmCountStatisticsData['completeCount'] }}</div>
        </div>
      </div>
      <!-- 柱状图 -->
      <div class="fault-chart">
        <ml-echarts id="faultChart" :option="chartOption" />
      </div>
      <!-- 告警列表表头 -->
      <div class="fault-table-header">
        <span class="fault-table-col">阿米巴</span>
        <span class="fault-table-col">告警等级</span>
        <span class="fault-table-col fault-table-col--content">告警内容</span>
        <span class="fault-table-col">发生时间</span>
      </div>
      <!-- 告警列表 -->
      <div class="fault-table-body">
        <vue-seamless-scroll
          :data="deviceErrorRecordsData"
          :class-option="{ limitMoveNum: 1, step: 0.4, hoverStop: true }"
          :style="{ height: '150px' }"
        >
          <div class="fault-table-row" v-for="(item, idx) in deviceErrorRecordsData" :key="idx">
            <span class="fault-table-col">
              <i class="fault-row-dot"></i>{{ item.companyName }}
            </span>
            <span class="fault-table-col">
              <span class="fault-badge" :class="'fault-badge--' + getSeverityType(item.severity)">
                {{ item.severity }}
              </span>
            </span>
            <span class="fault-table-col fault-table-col--content" :title="item.title">
              {{ item.title }}
            </span>
            <span class="fault-table-col">{{ item.submitTime }}</span>
          </div>
        </vue-seamless-scroll>
      </div>
    </CardBox>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import * as echarts from 'echarts'
import { getTabberData, animaction } from '@utils'
import CardBox from './cardBox.vue'
import faultItemBg from '@assets/image/homeIndex/fault-item_bg.png'
import z5Option from '@utils/viewData/z5'
import vueSeamlessScroll from 'vue-seamless-scroll/src'

export default {
  name: 'FaultStatisticsComponent',
  components: { CardBox, vueSeamlessScroll },
  setup() {
    const { dispatch } = useStore()

    // 故障告警数量统计
    const errorAlarmCountStatisticsData = ref({})
    const getErrorAlarmCountStatistics = async () => {
      const { data } = await getTabberData(dispatch, 'fetchErrorAlarmCountStatistics')
      errorAlarmCountStatisticsData.value = data || {}
    }

    // 故障程度数量统计
    const errorLevelCountStatisticsData = ref([])
    const errorLevelCountStatisticsIndex = ref([])
    const errorLevelCountStatisticsInterval = ref()
    const getErrorLevelCountStatistics = async () => {
      const { data } = await getTabberData(dispatch, 'fetchErrorLevelCountStatistics')
      errorLevelCountStatisticsData.value = data || []
      errorLevelCountStatisticsInterval.value = animaction(data, errorLevelCountStatisticsIndex)
    }

    // 告警列表
    const deviceErrorRecordsData = ref([])
    const getFaultServiceCenterTabberData = async () => {
      const { data } = await getTabberData(dispatch, 'fetchNewErrorRecordByCompany')
      if (data && data.length > 0) {
        const nData = data.map(item => ({
          companyName: item.companyName.replaceAll('阿米巴', ''),
          severity: item.severity,
          title: item.title,
          submitTime: item.submitTime.split(' ')[0]
        }))
        deviceErrorRecordsData.value = nData
      }
    }

    // 告警等级样式映射
    const getSeverityType = severity => {
      if (!severity) return 'default'
      if (severity === '轻微') return 'minor'
      if (severity === '一般') return 'normal'
      if (severity === '严重') return 'severe'
      if (severity === '灾难') return 'disaster'
      return 'default'
    }

    // 供父组件调用的定时刷新方法（先清除动画定时器再重新拉取）
    const getInit = () => {
      getErrorAlarmCountStatistics()
      getErrorLevelCountStatistics()
      getFaultServiceCenterTabberData()
    }
    const closeTimeInit = () => {
      clearInterval(errorLevelCountStatisticsInterval.value)
    }
    const refreshData = () => {
      closeTimeInit()
      getInit()
    }

    onMounted(() => {
      getInit()
    })

    return {
      errorAlarmCountStatisticsData,
      errorLevelCountStatisticsData,
      errorLevelCountStatisticsIndex,
      deviceErrorRecordsData,
      faultItemBg,
      z5Option,
      getSeverityType,
      refreshData
    }
  },
  computed: {
    chartOption() {
      const base = this.z5Option(
        this.errorLevelCountStatisticsData,
        this.errorLevelCountStatisticsIndex
      )
      // 图例渐变色（与柱子一致）
      const legendGradients = [
        new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#33cdff' },
          { offset: 0.5, color: '#33cdff' },
          { offset: 1, color: 'rgba(51,205,255,0.30)' }
        ]),
        new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#21e892' },
          { offset: 0.09, color: '#21e892' },
          { offset: 1, color: 'rgba(33,232,146,0.30)' }
        ]),
        new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#ffc000' },
          { offset: 0.09, color: '#ffc000' },
          { offset: 1, color: 'rgba(255,192,0,0.30)' }
        ]),
        new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#ff8443' },
          { offset: 0.09, color: '#ff8443' },
          { offset: 1, color: 'rgba(255,132,67,0.30)' }
        ])
      ]
      // 恢复内置图例，渐变色与柱子一致
      base.legend = {
        show: true,
        top: 0,
        textStyle: { color: '#fff', fontSize: 12 },
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
        data: [
          { name: '轻微', itemStyle: { color: legendGradients[0] } },
          { name: '一般', itemStyle: { color: legendGradients[1] } },
          { name: '严重', itemStyle: { color: legendGradients[2] } },
          { name: '灾难', itemStyle: { color: legendGradients[3] } }
        ]
      }
      // 开启 Y 轴网格线 + 轴线/文字颜色统一 #D5EAFF
      const axisColor = '#D5EAFF'
      if (base.xAxis && base.xAxis[0]) {
        base.xAxis[0].axisLine.lineStyle.color = axisColor
        base.xAxis[0].axisTick.show = false
        base.xAxis[0].axisLabel.color = axisColor
      }
      if (base.yAxis && base.yAxis[0]) {
        base.yAxis[0].axisLine = { show: true, lineStyle: { color: axisColor } }
        base.yAxis[0].axisTick = { show: false }
        base.yAxis[0].axisLabel.color = axisColor
        base.yAxis[0].splitLine = {
          show: true,
          lineStyle: { color: 'rgba(50, 80, 140, 0.4)', type: 'dashed' }
        }
      }
      // 加宽柱子 + 渐变色
      const gradientColors = [
        // 轻微
        [
          { offset: 0, color: '#33cdff' },
          { offset: 0.5, color: '#33cdff' },
          { offset: 1, color: 'rgba(51,205,255,0.30)' }
        ],
        // 一般
        [
          { offset: 0, color: '#21e892' },
          { offset: 0.09, color: '#21e892' },
          { offset: 1, color: 'rgba(33,232,146,0.30)' }
        ],
        // 严重
        [
          { offset: 0, color: '#ffc000' },
          { offset: 0.09, color: '#ffc000' },
          { offset: 1, color: 'rgba(255,192,0,0.30)' }
        ],
        // 灾难
        [
          { offset: 0, color: '#ff8443' },
          { offset: 0.09, color: '#ff8443' },
          { offset: 1, color: 'rgba(255,132,67,0.30)' }
        ]
      ]
      if (base.series) {
        base.series.forEach((s, i) => {
          s.barWidth = '30px'
          if (gradientColors[i]) {
            s.itemStyle = {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, gradientColors[i])
            }
          }
        })
      }
      // 调整 grid 留出更多图表空间（顶部留出图例位置）
      base.grid = { top: '30px', left: '2%', right: '4%', bottom: '10px', containLabel: true }
      return base
    }
  }
}
</script>

<style lang="scss" scoped>
.fault-container {
  width: 100%;
  height: calc(55% - 10px);
  margin-bottom: 20px;
  overflow: hidden;
}

/* 三个统计卡片 */
.fault-cards {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}
.fault-card {
  flex: 1;
  width: 128px;
  height: 57px;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &__label {
    height: 24px;
    line-height: 24px;
    text-align: center;
    font-size: 12px;
    color: #d5eaff;
    font-family: 'SourceHanSansCN-Regular', sans-serif;
  }
  &__value {
    height: 32px;
    line-height: 32px;
    text-align: center;
    font-size: 16px;
    color: #fff;
    font-family: 'OPPOSans-B', sans-serif;
  }
}

/* 图表 */
.fault-chart {
  width: 100%;
  height: calc((100% - 57px) / 2);
  margin-bottom: 10px;
}

/* 告警表格 */
.fault-table-header {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 10px;
  background: rgba(0, 128, 255, 0.5);
  border-radius: 4px 4px 0 0;
  margin-bottom: 2px;
}
.fault-table-col {
  flex: 1;
  font-size: 13px;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--content {
    flex: 1.5;
    text-align: left;
    padding-left: 8px;
  }
}
.fault-table-body {
  width: 100%;
  overflow: hidden;
}
.fault-table-row {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 10px;

  &:nth-child(odd) {
    background: rgba(0, 128, 255, 0.2);
  }
  &:nth-child(even) {
    background: rgba(0, 128, 255, 0.05);
  }
}
.fault-row-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3ba0ff;
  margin-right: 6px;
  vertical-align: middle;
}

/* 告警等级徽章 */
.fault-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;

  &--minor {
    background: rgba(68, 137, 249, 0.25);
    color: #4489f9;
  }
  &--normal {
    background: rgba(31, 203, 48, 0.25);
    color: #1fcb30;
  }
  &--severe {
    background: rgba(242, 181, 55, 0.25);
    color: #f2b537;
  }
  &--disaster {
    background: rgba(247, 121, 90, 0.25);
    color: #f7795a;
  }
  &--default {
    background: rgba(200, 220, 255, 0.1);
    color: rgba(200, 220, 255, 0.7);
  }
}
</style>
